import i18n from 'i18next';
import { axiosAzureAPI } from './axiosService';
import { FeedListResponse, FeedPost, FeedCategory, FeedSource, FeedFilters } from '../models/feed';

export const feedService = {
    // Get list of posts (news or events)
    async getPosts(
        type: 'news' | 'event',
        page: number = 1,
        pageSize: number = 20,
        filters?: FeedFilters
    ): Promise<FeedListResponse> {
        const params: any = {
            type,
            page,
            pageSize,
        };

        // Only include filter params that are truthy (matches mobile app behavior)
        // Don't send onlyEvents=false, just omit it entirely
        if (filters) {
            if (filters.search) params.search = filters.search;
            if (filters.categoryId) params.categoryId = filters.categoryId;
            if (filters.sourceName) params.sourceName = filters.sourceName;
            if (filters.onlyUrgent) params.onlyUrgent = filters.onlyUrgent;
            if (filters.onlyEvents) {
                params.onlyEvents = filters.onlyEvents;
                params.sortBy = 'eventDate';
                params.sortOrder = 'asc'; // Soonest first for upcoming events
            }
            if (filters.sortBy) params.sortBy = filters.sortBy;
            if (filters.sortOrder) params.sortOrder = filters.sortOrder;
        }

        // For events without onlyEvents filter, still sort by event date (latest first)
        if (type === 'event' && !params.sortBy) {
            params.sortBy = 'eventDate';
            params.sortOrder = 'desc';
        }

        // Add language parameter for translations (matching mobile app)
        const currentLanguage = i18n.language || 'en';
        if (currentLanguage !== 'en') {
            params.language = currentLanguage;
        }

        const response = await axiosAzureAPI.get('/api/feed/posts', { params });
        return response.data;
    },

    // Get single post by ID
    async getPostById(id: string): Promise<FeedPost> {
        const params: any = {};
        const currentLanguage = i18n.language || 'en';
        if (currentLanguage !== 'en') {
            params.language = currentLanguage;
        }
        const response = await axiosAzureAPI.get(`/api/feed/posts/${id}`, { params });
        return response.data;
    },

    // Get related posts
    async getRelatedPosts(id: string, limit: number = 3): Promise<FeedPost[]> {
        const params: any = { limit };
        const currentLanguage = i18n.language || 'en';
        if (currentLanguage !== 'en') {
            params.language = currentLanguage;
        }
        const response = await axiosAzureAPI.get(`/api/feed/posts/${id}/related`, { params });
        return response.data;
    },

    // Get categories
    async getCategories(): Promise<FeedCategory[]> {
        const response = await axiosAzureAPI.get('/api/feed/categories');
        return response.data;
    },

    // Get sources
    async getSources(): Promise<FeedSource[]> {
        const response = await axiosAzureAPI.get('/api/feed/sources');
        return response.data;
    },
};
